import { useState, useEffect, useContext } from "react";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Drawer,
} from "@mui/material";
import { NavLink } from "react-router-dom";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import DomainOutlinedIcon from "@mui/icons-material/DomainOutlined";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
// import HistoryEduOutlinedIcon from "@mui/icons-material/HistoryEduOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
// import ContactsOutlinedIcon from "@mui/icons-material/ContactsOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import FingerprintOutlinedIcon from "@mui/icons-material/FingerprintOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import { useThemeColors } from "../context/ThemeContext";
import { AuthContext } from "../context/AuthContext";
import { useDomains } from "../hooks/queries";
import { daysUntil } from "../utils/expiry";
import { COLORS } from "../theme/tokens";
import logo from "../assets/image/logo.png.webp";

// Nav is organised into spec groups (Infrastructure / Monitoring / Account) with
// uppercase group labels — matching Section 3 of the RABS Workspace spec. The
// first group has no label (Dashboard sits at the top on its own).
//
// Per item:
//   `module`   — ties the item to a permission module (gated by its `view`).
//   `adminOnly`— shows only for admins.
//   `badgeKey` — renders a live count pill (currently "expiry" = domains due ≤30d).
// Items with no gate are always shown.
const NAV_GROUPS = [
  {
    label: null,
    items: [
      {
        to: "/dashboard",
        label: "Dashboard",
        icon: <DashboardOutlinedIcon sx={{ fontSize: 22 }} />,
      },
    ],
  },
  {
    label: "Infrastructure",
    items: [
      {
        to: "/domains",
        label: "Domains",
        module: "domains",
        icon: <DomainOutlinedIcon sx={{ fontSize: 22 }} />,
      },
      {
        to: "/servers",
        label: "Servers",
        module: "servers",
        icon: <DnsOutlinedIcon sx={{ fontSize: 22 }} />,
      },
      {
        to: "/category",
        label: "Category",
        module: "categories",
        icon: <CategoryOutlinedIcon sx={{ fontSize: 22 }} />,
      },
      {
        to: "/trash",
        label: "Recycle Bin",
        icon: <DeleteSweepOutlinedIcon sx={{ fontSize: 22 }} />,
      },
    ],
  },
  {
    label: "Monitoring",
    items: [
      {
        to: "/domain-monitors",
        label: "Domain Monitors",
        module: "monitors",
        icon: <MonitorHeartOutlinedIcon sx={{ fontSize: 22 }} />,
      },
      {
        to: "/expiry-alerts",
        label: "Expiry Alerts",
        module: "alerts",
        badgeKey: "expiry",
        icon: <NotificationsActiveOutlinedIcon sx={{ fontSize: 22 }} />,
      },
      // {
      // to: "/activity-log",
      // label: "Activity Log",
      // module: "logs",
      // icon: <HistoryEduOutlinedIcon sx={{ fontSize: 22 }} />,
      // },
    ],
  },
  {
    label: "Attendance",
    items: [
      {
        to: "/attendance",
        end: true,
        label: "Today",
        module: "attendance",
        icon: <FingerprintOutlinedIcon sx={{ fontSize: 22 }} />,
      },
      {
        to: "/attendance/daily",
        label: "Daily Report",
        module: "attendance",
        icon: <EventNoteOutlinedIcon sx={{ fontSize: 22 }} />,
      },
      {
        to: "/attendance/monthly",
        label: "Monthly Register",
        module: "attendance",
        icon: <CalendarMonthOutlinedIcon sx={{ fontSize: 22 }} />,
      },
    ],
  },
  {
    label: "Account",
    items: [
      {
        to: "/users",
        label: "Users",
        adminOnly: true,
        icon: <PeopleAltOutlinedIcon sx={{ fontSize: 22 }} />,
      },
      {
        to: "/invoices",
        label: "Invoices",
        module: "invoices",
        icon: <ReceiptLongOutlinedIcon sx={{ fontSize: 22 }} />,
      },
      {
        to: "/bank-accounts",
        label: "Bank Accounts",
        module: "bankAccounts",
        icon: <AccountBalanceOutlinedIcon sx={{ fontSize: 22 }} />,
      },
    ],
  },
];

function Sidebar({ mobileOpen = false, onMobileClose }) {
  const colors = useThemeColors();
  const { isAdmin, hasPermission } = useContext(AuthContext);

  // Live count for the Expiry Alerts badge: domains due within 30 days (incl.
  // overdue). Reuses the app-wide cached /domains query — no extra fetch where
  // it's already loaded (Dashboard, Domains list).
  const { data: domainsData } = useDomains();
  const domains = Array.isArray(domainsData)
    ? domainsData
    : domainsData?.rows || [];
  const badgeCounts = {
    expiry: domains.filter((d) => {
      const days = daysUntil(d.expiry);
      return days !== null && days <= 30;
    }).length,
  };

  // Keep only the items this user may see, then drop any group left empty.
  const visibleGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => {
      if (item.adminOnly) return isAdmin;
      if (item.module) return hasPermission(item.module, "view");
      return true;
    }),
  })).filter((group) => group.items.length > 0);

  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("sidebarCollapsed") === "true"
  );

  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", collapsed);
  }, [collapsed]);

  // A single nav row (icon + label + optional badge). Shared by both layouts.
  function renderItem(item, isCollapsed, inDrawer) {
    const badge = item.badgeKey ? badgeCounts[item.badgeKey] || 0 : 0;
    return (
      <Tooltip
        key={item.to}
        title={isCollapsed ? item.label : ""}
        placement="right"
        disableHoverListener={!isCollapsed}
      >
        <Box
          component={NavLink}
          to={item.to}
          end={item.end}
          onClick={inDrawer ? onMobileClose : undefined}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: isCollapsed ? "center" : "flex-start",
            gap: 1.5,
            px: isCollapsed ? 0 : 1.75,
            py: 1.25,
            borderRadius: "10px",
            textDecoration: "none",
            fontFamily:
              "'Work Sans', 'Work Sans Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            fontSize: 14,
            fontWeight: 600,
            letterSpacing: "-0.2px",
            color: COLORS.muted,
            whiteSpace: "nowrap",
            position: "relative",
            transition: "all 0.15s ease",
            "&:hover": {
              background: COLORS.tile,
              color: COLORS.ink,
            },
            "&.active": {
              color: COLORS.primary,
              background: COLORS.primarySoft,
              fontWeight: 700,
              "&::before": {
                content: '""',
                position: "absolute",
                left: 0,
                top: "22%",
                bottom: "22%",
                width: "3px",
                borderRadius: "0 3px 3px 0",
                background: COLORS.primary,
              },
              "&:hover": {
                background: "#E0ECFE",
              },
            },
          }}
        >
          {/* Icon (with a corner badge when collapsed) */}
          {isCollapsed ? (
            <Box sx={{ position: "relative", display: "flex" }}>
              {item.icon}
              {badge > 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: -5,
                    right: -7,
                    minWidth: 15,
                    height: 15,
                    px: 0.4,
                    borderRadius: "999px",
                    background: COLORS.primary,
                    color: "#fff",
                    fontSize: 9,
                    fontWeight: 700,
                    lineHeight: "15px",
                    textAlign: "center",
                  }}
                >
                  {badge > 99 ? "99+" : badge}
                </Box>
              )}
            </Box>
          ) : (
            item.icon
          )}

          {!isCollapsed && (
            <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
              {item.label}
            </Box>
          )}

          {/* Count pill (expanded layout) */}
          {!isCollapsed && badge > 0 && (
            <Box
              component="span"
              sx={{
                flexShrink: 0,
                minWidth: 20,
                height: 20,
                px: 0.6,
                borderRadius: "999px",
                background: COLORS.primary,
                color: "#fff",
                fontSize: 11,
                fontWeight: 700,
                lineHeight: "20px",
                textAlign: "center",
              }}
            >
              {badge > 99 ? "99+" : badge}
            </Box>
          )}
        </Box>
      </Tooltip>
    );
  }

  // The inner sidebar UI, shared by the desktop sticky rail and the mobile
  // drawer. `inDrawer` forces the expanded layout (no collapse toggle) and
  // closes the drawer when a nav link is tapped.
  function renderContent(inDrawer) {
    const isCollapsed = inDrawer ? false : collapsed;
    return (
      <>
        {/* Brand + collapse toggle */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent={isCollapsed ? "center" : "space-between"}
          sx={{
            px: isCollapsed ? 1 : 2,
            py: 2,
            gap: 1.5,
            borderBottom: `1px solid ${COLORS.hairline}`,
          }}
        >
          {!isCollapsed && (
            <Box
              component="img"
              src={logo}
              alt="RABS Net Solutions Pvt. Ltd."
              sx={{
                flex: 1,
                minWidth: 0,
                width: "auto",
                maxWidth: "100%",
                objectFit: "contain",
                display: "block",
              }}
            />
          )}

          {/* Desktop: collapse toggle. Mobile drawer: this button isn't shown
              because the drawer closes via backdrop tap / link tap. */}
          {!inDrawer && (
            <Tooltip
              title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              placement="right"
            >
              <IconButton
                onClick={() => setCollapsed(!collapsed)}
                sx={{
                  flexShrink: 0,
                  color: colors.textMuted,
                  border: `1px solid ${colors.cardBorder}`,
                  borderRadius: 2,
                  "& .MuiSvgIcon-root": { fontSize: 30 },
                  "&:hover": {
                    background: colors.hover,
                    color: colors.text,
                    borderColor: colors.cardBorderHover,
                  },
                }}
              >
                {isCollapsed ? <MenuIcon /> : <MenuOpenIcon />}
              </IconButton>
            </Tooltip>
          )}
        </Stack>

        {/* Grouped nav */}
        {/* Scrolls on its own when the groups are taller than the screen, so
            the bottom items (Invoices, Bank Accounts) are never cut off; the
            logo row above stays pinned. */}
        <Stack
          sx={{
            px: isCollapsed ? 1.5 : 2,
            pt: 1,
            pb: 2,
            gap: 0.25,
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            scrollbarWidth: "thin",
            scrollbarColor: `${COLORS.hairlineStrong} transparent`,
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": {
              background: COLORS.hairlineStrong,
              borderRadius: 3,
            },
          }}
        >
          {visibleGroups.map((group, gi) => (
            <Box key={group.label || `group-${gi}`} sx={{ mb: 0.75 }}>
              {/* Group label (expanded) or a hairline separator (collapsed) */}
              {group.label &&
                (isCollapsed ? (
                  gi > 0 && (
                    <Box
                      sx={{
                        height: "1px",
                        background: COLORS.hairline,
                        mx: 0.5,
                        my: 1,
                      }}
                    />
                  )
                ) : (
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: COLORS.faint,
                      textTransform: "uppercase",
                      letterSpacing: "0.7px",
                      px: 1.75,
                      mt: gi > 0 ? 1.25 : 0,
                      mb: 0.5,
                    }}
                  >
                    {group.label}
                  </Typography>
                ))}

              <Stack sx={{ gap: 0.5 }}>
                {group.items.map((item) =>
                  renderItem(item, isCollapsed, inDrawer)
                )}
              </Stack>
            </Box>
          ))}
        </Stack>
      </>
    );
  }

  const width = collapsed ? 76 : 260;

  return (
    <>
      {/* Desktop sticky rail — hidden on mobile (xs/sm) */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          width,
          flexShrink: 0,
          height: "100vh",
          position: "sticky",
          top: 0,
          background: "#ffffff",
          borderRight: `1px solid ${COLORS.hairline}`,
          flexDirection: "column",
          overflow: "hidden",
          transition:
            "width 0.25s ease, background 0.2s ease, border-color 0.2s ease",
        }}
      >
        {renderContent(false)}
      </Box>

      {/* Mobile off-canvas drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: 260,
            boxSizing: "border-box",
            background: "#ffffff",
            borderRight: `1px solid ${COLORS.hairline}`,
            backgroundImage: "none",
          },
        }}
      >
        {renderContent(true)}
      </Drawer>
    </>
  );
}

export default Sidebar;
